import { Redirect, type RouteComponentProps } from "react-router";

import { routes } from "utilities/routes";

import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { withJobPrompt } from "commonComponents/helpers/JobPrompt/JobPrompt";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";

import { type ISelectionListContainerHandler } from "entity/selection/SelectionListContainer/SelectionListContainer.api.handler";
import { useSelectionConfig } from "entity/selection/SelectionListContainer/useSelectionConfig";

export interface ISelectionQuickAddProps extends RouteComponentProps {
    jobId: number;
    modalConfig: IModalConfiguration;
    // Injectable for tests; defaults to the real SelectionListContainerHandler inside the hook.
    selectionListContainerHandler?: ISelectionListContainerHandler;
}

// Chooses the create flow for a quick add selection based on the job level flag
// rendered with a valid jobId, withJobPrompt shows the job picker if not
const SelectionQuickAddDispatcher = (props: ISelectionQuickAddProps) => {
    const { jobId, selectionListContainerHandler } = props;
    const { config, isLoading } = useSelectionConfig(jobId, selectionListContainerHandler);

    if (isLoading) {
        return <BTLoading />;
    }

    const target = config?.isSelectionsReimaginedJob
        ? routes.selectionOptions.getDetailsLink(0, jobId, "Create", 0, true)
        : routes.selection.getDetailsLink(0, jobId);

    // replace so this interstitial URL doesn't land in history.
    return <Redirect to={`${props.modalConfig.parentRoute}${target}`} push={false} />;
};

export const SelectionQuickAdd = withJobPrompt(SelectionQuickAddDispatcher, {
    // routeTransform gets the full prop set, modalConfig included - re-enter this same route
    // for the picked job by rebuilding from parentRoute rather than editing the current URL.
    routeTransform: (props) =>
        `${props.modalConfig.parentRoute}${routes.selection.getQuickAddLink(props.jobId)}`,
});

/**
 * Handles `SelectionQuickAdd/:jobId` relative to the `/QuickAction` block, so the live URL is
 * `<page>/QuickAction/SelectionQuickAdd/:jobId`. `jobId` is 0 when no job is selected, which is
 * what makes withJobPrompt show the job picker before the dispatcher decides where to send the user.
 */
export const RouteSelectionQuickAdd = (props: {
    modalConfig: { beforeClose: () => void; parentRoute: string };
}) => (
    <RouteRelative
        path={routes.selection.quickAdd}
        render={(routeProps: RouteComponentProps<{ jobId: string }>) => (
            <SelectionQuickAdd
                {...routeProps}
                modalConfig={props.modalConfig}
                jobId={parseInt(routeProps.match.params.jobId)}
            />
        )}
    />
);
